import {Component, HostListener, Input, OnChanges, OnInit, SimpleChanges, TemplateRef} from '@angular/core';
import {CdkDragEnd} from '@angular/cdk/drag-drop';

interface WidgetConfig {
  key: string;
  disposition: {x: number; y: number, cols: number, rows: number};
}

interface DashboardGridWidgetConfig {
  [key: string]: WidgetConfig;
}

@Component({
  selector: 'app-dashboard-grid',
  templateUrl: './dashboard-grid.component.html',
  styleUrls: ['./dashboard-grid.component.scss']
})
export class DashboardGridComponent implements OnInit, OnChanges {
  colCount = 8;
  rowCount = 10;

  movingWidget: any = null;
  previewPosition: {x: number; y: number} | null = null;
  validPositions: Set<string> = new Set();
  occupiedCells: Set<string> = new Set();
  previewCells: Set<string> = new Set();

  @Input() editable: boolean = true;
  @Input() editMode: boolean = false;
  @Input() widgetConfig: DashboardGridWidgetConfig = {};
  @Input() widgetTemplate: TemplateRef<any>;


  widgets: {id: any, disposition: {x: number; y: number, cols: number, rows: number}, key: string}[] = [];

  ngOnInit() {
    this.initWidgets(this.widgetConfig);
  }

  ngOnChanges(changes: SimpleChanges) {
    if(changes.widgetConfig) {
      this.initWidgets(changes.widgetConfig.currentValue);
    }
  }

  initWidgets(widgetConfig) {
    this.widgets = Object.entries(widgetConfig || {}).reduce((array, [id, config]: [string, WidgetConfig]) => {
      array.push({
        id,
        key: config.key,
        disposition: config.disposition
      });
      return array;
    }, []);

    const maxRow = this.widgets.reduce((max, widget) => {
      const bottomEdge = widget.disposition.y + widget.disposition.rows;
      return Math.max(max, bottomEdge);
    }, 0);

    this.rowCount = maxRow + 4;
  }

  enterMoveMode(widget: any) {
    this.movingWidget = widget;
    this.previewPosition = null;
    this.validPositions.clear();
    this.previewCells.clear();
    this.occupiedCells.clear();

    const cols = widget.disposition.cols;
    const rows = widget.disposition.rows;

    // Calculamos celdas ocupadas (para visualización)
    const occupied = this.getOccupiedMap(widget);
    for (let y = 0; y < this.rowCount; y++) {
      for (let x = 0; x < this.colCount; x++) {
        if (occupied[y][x]) {
          this.occupiedCells.add(`${x},${y}`);
        }
      }
    }


    // Calculamos posiciones válidas (solo por límite del grid)
    for (let y = 0; y <= this.rowCount - rows; y++) {
      for (let x = 0; x <= this.colCount - cols; x++) {
        if (!occupied[y][x])
          this.validPositions.add(`${x},${y}`);
      }
    }

  }

  cancelMove() {
    this.movingWidget = null;
    this.previewPosition = null;
  }

  setPreview(x: number, y: number) {
    this.previewPosition = {x, y};
    this.previewCells.clear();

    if (!this.movingWidget) return;

    const cols = this.movingWidget.disposition.cols;
    const rows = this.movingWidget.disposition.rows;

    for (let dy = 0; dy < rows; dy++) {
      for (let dx = 0; dx < cols; dx++) {
        this.previewCells.add(`${x + dx},${y + dy}`);
      }
    }
  }

  confirmMove(x: number, y: number) {
    if (!this.movingWidget || !this.validPositions.has(x + ',' + y)) return;

    this.movingWidget.disposition.x = x;
    this.movingWidget.disposition.y = y;

    // this.pushWidgets(this.movingWidget); // 👈 Desplaza los que estén en su camino

    this.cancelMove();
  }


  @HostListener('document:keydown.escape')
  onEscape() {
    this.cancelMove();
  }

  getOccupiedMap(excludeWidget?: any): boolean[][] {
    const map: boolean[][] = Array.from({length: this.rowCount}, () =>
      Array(this.colCount).fill(false)
    );

    for (const widget of this.widgets) {
      if (widget === excludeWidget) continue;
      for (let dy = 0; dy < widget.disposition.rows; dy++) {
        for (let dx = 0; dx < widget.disposition.cols; dx++) {
          const x = widget.disposition.x + dx;
          const y = widget.disposition.y + dy;
          if (x < this.colCount && y < this.rowCount) {
            map[y][x] = true;
          }
        }
      }
    }

    return map;
  }

  pushWidgets(moved: any) {
    const destination = {x: moved.disposition.x, y: moved.disposition.y};

    // 1. Ordenar widgets excluyendo el que se mueve
    const otherWidgets = this.widgets
      .filter(w => w.id !== moved.id)
      .sort((a, b) => this.compareWidgetPosition(a.disposition, b.disposition));

    const widgetsToReposition: any[] = [];

    // 2. Crear el mapa de ocupación y ocupar todos los widgets que están antes del destino
    const occupied: boolean[][] = Array.from({length: this.rowCount}, () =>
      Array(this.colCount).fill(false)
    );

    for (const widget of otherWidgets) {
      const widgetPos = {x: widget.disposition.x, y: widget.disposition.y};

      if (this.compareWidgetPosition(widgetPos, destination) < 0) {
        // El widget está antes del destino → no se toca
        this.occupy(widget.disposition.x, widget.disposition.y, widget.disposition.cols, widget.disposition.rows, occupied);
      } else {
        // Widget después del destino → hay que reubicarlo
        widgetsToReposition.push(widget);
      }
    }

    // 3. Ocupar la posición destino del widget movido
    this.occupy(moved.disposition.x, moved.disposition.y, moved.disposition.cols, moved.disposition.rows, occupied);

    // 4. Reubicar los que vienen después
    for (const widget of widgetsToReposition) {
      const spot = this.findNextAvailableSpot(widget, occupied);
      if (spot) {
        widget.disposition.x = spot.x;
        widget.disposition.y = spot.y;
        this.occupy(spot.x, spot.y, widget.disposition.cols, widget.disposition.rows, occupied);
      } else {
        console.warn(`No se pudo reubicar el widget ID: ${widget.id}`);
      }
    }
  }

  compareWidgetPosition(a: {x: number; y: number}, b: {x: number; y: number}): number {
    return a.y !== b.y ? a.y - b.y : a.x - b.x;
  }

  findNextAvailableSpot(widget: any, occupied: boolean[][]): {x: number; y: number} | null {
    for (let y = 0; y < this.rowCount; y++) {
      for (let x = 0; x < this.colCount; x++) {
        if (this.canFit(x, y, widget.disposition.cols, widget.disposition.rows, occupied)) {
          return {x, y};
        }
      }
    }
    return null;
  }

  canFit(x: number, y: number, cols: number, rows: number, occupied: boolean[][]): boolean {
    if (x + cols > this.colCount || y + rows > this.rowCount) return false;
    for (let dy = 0; dy < rows; dy++) {
      for (let dx = 0; dx < cols; dx++) {
        if (occupied[y + dy][x + dx]) return false;
      }
    }
    return true;
  }


  occupy(x: number, y: number, cols: number, rows: number, occupied: boolean[][]): void {
    for (let dy = 0; dy < rows; dy++) {
      for (let dx = 0; dx < cols; dx++) {
        occupied[y + dy][x + dx] = true;
      }
    }
  }

  getCol(i: number): number {
    return i % this.colCount;
  }

  getRow(i: number): number {
    return Math.floor(i / this.colCount);
  }

  // DRAG

  onDragStart(widget: any) {
    this.enterMoveMode(widget);
  }

  onDragEnd(event: CdkDragEnd, widget: any) {
    if (this.previewPosition && this.validPositions.has(this.previewPosition.x + ',' + this.previewPosition.y)) {
      this.confirmMove(this.previewPosition.x, this.previewPosition.y);
    } else {
      this.cancelMove();
    }
    event.source.reset();

  }


}
